import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, ChevronUp, Eye, EyeOff, Download, Pencil } from 'lucide-react';
import { BookingWithCustomer, getBookingStatus, statusColors, formatDate, getDaysDuration } from '../../utils/bookingViewUtils';
import Papa from 'papaparse';
import { useAuth } from '../../hooks/useAuth';

/** Row shape for course-session table view (Bookings → sessions list). */
export type SessionListTableRow = {
  sessionId: string;
  ref: string;
  courseName: string;
  dates: string;
  /** ISO date (YYYY-MM-DD) for grouping/sorting; empty when unknown */
  firstDateIso: string;
  firstDate: string;
  dayCount: number;
  startTime: string;
  endTime: string;
  language: string;
  deliveryMode: string;
  location: string;
  instructor: string;
  interpreter: string;
  provider: string;
  capacity: number;
  booked: number;
  virtualSeats: number;
  availableSeats: number;
  status: string;
  feeTargetEur: number;
  collectedEur: number;
  paidCount: number;
  halfPaidCount: number;
  unpaidCount: number;
  docsCount: number;
  documentsPrep: string;
  certificatesPrep: string;
  opsHealth: string;
  opsBlockers: number;
  opsUnpaidAttendees: number;
  missingCompliance: number;
  postChecklistMissing: number;
  notes: string;
  createdBy: string;
  cancellationReason: string;
  multiday: string;
};

type SessionColumnKey = keyof Omit<SessionListTableRow, 'sessionId'>;

const SESSION_TABLE_PREFS_KEY = 'bookings_sessions_table_prefs_v1';
const LEGACY_COLUMNS_KEY = 'bookings_sessions_table_columns_v1';

type SessionTablePrefs = {
  visibleColumns: Partial<Record<SessionColumnKey, boolean>>;
  expandedDateGroups?: string[];
};

function sessionPrefsStorageKey(userId?: string): string {
  return userId ? `${SESSION_TABLE_PREFS_KEY}:${userId}` : SESSION_TABLE_PREFS_KEY;
}

function readSessionTablePrefs(userId?: string): { visible: Set<SessionColumnKey>; expanded: Set<string> } {
  const allKeys = SESSION_COLUMNS.map((c) => c.key);
  const fallback = () => ({ visible: new Set(allKeys), expanded: new Set<string>() });

  const tryKeys = userId
    ? [sessionPrefsStorageKey(userId), SESSION_TABLE_PREFS_KEY, LEGACY_COLUMNS_KEY]
    : [SESSION_TABLE_PREFS_KEY, LEGACY_COLUMNS_KEY];

  for (const key of tryKeys) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw) as SessionTablePrefs | Partial<Record<SessionColumnKey, boolean>>;
      const visibleRecord =
        parsed && typeof parsed === 'object' && 'visibleColumns' in parsed
          ? (parsed as SessionTablePrefs).visibleColumns
          : (parsed as Partial<Record<SessionColumnKey, boolean>>);
      const visibleKeys = SESSION_COLUMNS.filter((c) => visibleRecord?.[c.key] !== false).map((c) => c.key);
      const expanded =
        parsed && typeof parsed === 'object' && 'expandedDateGroups' in parsed
          ? (parsed as SessionTablePrefs).expandedDateGroups || []
          : [];
      return {
        visible: visibleKeys.length > 0 ? new Set(visibleKeys) : new Set(allKeys),
        expanded: new Set(expanded),
      };
    } catch {
      /* next key */
    }
  }
  return fallback();
}

const SESSION_COLUMNS: { key: SessionColumnKey; label: string }[] = [
  { key: 'ref', label: 'Ref' },
  { key: 'courseName', label: 'Course' },
  { key: 'dates', label: 'All dates' },
  { key: 'firstDate', label: 'First day' },
  { key: 'dayCount', label: 'Days' },
  { key: 'startTime', label: 'Start' },
  { key: 'endTime', label: 'End' },
  { key: 'language', label: 'Language' },
  { key: 'deliveryMode', label: 'Delivery' },
  { key: 'location', label: 'Location' },
  { key: 'instructor', label: 'Instructor' },
  { key: 'interpreter', label: 'Interpreter' },
  { key: 'provider', label: 'Provider' },
  { key: 'capacity', label: 'Capacity' },
  { key: 'booked', label: 'Booked' },
  { key: 'virtualSeats', label: 'Virtual' },
  { key: 'availableSeats', label: 'Available' },
  { key: 'status', label: 'Status' },
  { key: 'feeTargetEur', label: 'Fee target EUR' },
  { key: 'collectedEur', label: 'Collected EUR' },
  { key: 'paidCount', label: 'Paid' },
  { key: 'halfPaidCount', label: 'Half paid' },
  { key: 'unpaidCount', label: 'Unpaid' },
  { key: 'docsCount', label: 'Docs' },
  { key: 'documentsPrep', label: 'Docs prep' },
  { key: 'certificatesPrep', label: 'Certs prep' },
  { key: 'opsHealth', label: 'Ops health' },
  { key: 'opsBlockers', label: 'Blockers' },
  { key: 'opsUnpaidAttendees', label: 'Unpaid attendees' },
  { key: 'missingCompliance', label: 'Compliance gaps' },
  { key: 'postChecklistMissing', label: 'Post checklist gaps' },
  { key: 'notes', label: 'Notes' },
  { key: 'createdBy', label: 'Created by' },
  { key: 'cancellationReason', label: 'Cancellation reason' },
  { key: 'multiday', label: 'Multiday' },
];

function compareDateGroupKeys(a: string, b: string): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  const today = new Date().toISOString().slice(0, 10);
  const aFuture = a >= today;
  const bFuture = b >= today;
  if (aFuture && bFuture) return a.localeCompare(b);
  if (!aFuture && !bFuture) return b.localeCompare(a);
  return aFuture ? -1 : 1;
}

function formatSessionCell(key: SessionColumnKey, row: SessionListTableRow): string {
  const v = row[key];
  if (typeof v === 'number') {
    if (key === 'feeTargetEur' || key === 'collectedEur') return v.toFixed(2);
    return String(v);
  }
  return String(v ?? '');
}

export interface SessionsTableViewProps {
  rows: SessionListTableRow[];
  selectedSessionId: string | null;
  onSelectSession: (sessionId: string) => void;
  onRestoreSession?: (sessionId: string) => void;
  onDeleteSession?: (sessionId: string) => void;
}

/** Course sessions table — same column-toggle pattern as legacy bookings TableView. */
export function SessionsTableView({
  rows,
  selectedSessionId,
  onSelectSession,
  onRestoreSession,
  onDeleteSession,
}: SessionsTableViewProps) {
  const { user } = useAuth();
  const userId = user?.id;

  const [visibleColumns, setVisibleColumns] = useState<Set<SessionColumnKey>>(
    () => readSessionTablePrefs(userId).visible
  );
  const [expandedDateGroups, setExpandedDateGroups] = useState<Set<string>>(
    () => readSessionTablePrefs(userId).expanded
  );

  useEffect(() => {
    const prefs = readSessionTablePrefs(userId);
    setVisibleColumns(prefs.visible);
    setExpandedDateGroups(prefs.expanded);
  }, [userId]);

  useEffect(() => {
    const payload: SessionTablePrefs = {
      visibleColumns: Object.fromEntries(
        SESSION_COLUMNS.map((c) => [c.key, visibleColumns.has(c.key)])
      ) as Record<SessionColumnKey, boolean>,
      expandedDateGroups: [...expandedDateGroups],
    };
    try {
      localStorage.setItem(sessionPrefsStorageKey(userId), JSON.stringify(payload));
    } catch {
      /* ignore */
    }
  }, [visibleColumns, expandedDateGroups, userId]);

  const visibleColumnsArray = SESSION_COLUMNS.filter((c) => visibleColumns.has(c.key));

  const toggleColumn = (key: SessionColumnKey) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleDateGroup = (iso: string) => {
    setExpandedDateGroups((prev) => {
      const next = new Set(prev);
      if (next.has(iso)) next.delete(iso);
      else next.add(iso);
      return next;
    });
  };

  const dateGroups = useMemo(() => {
    const byDate = new Map<string, SessionListTableRow[]>();
    for (const row of rows) {
      const key = row.firstDateIso || '';
      const list = byDate.get(key) || [];
      list.push(row);
      byDate.set(key, list);
    }
    return [...byDate.entries()]
      .sort(([a], [b]) => compareDateGroupKeys(a, b))
      .map(([iso, groupRows]) => ({
        iso,
        label: groupRows[0]?.firstDate || 'No date',
        rows: groupRows,
      }));
  }, [rows]);

  const exportToCSV = () => {
    const data = rows.map((r) => {
      const out: Record<string, string> = {};
      visibleColumnsArray.forEach((c) => {
        out[c.label] = formatSessionCell(c.key, r);
      });
      return out;
    });
    const csv = Papa.unparse(data);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `sessions-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-4">
        <div>
          <p className="text-sm font-semibold text-gray-900">Sessions table</p>
          <p className="text-xs text-gray-500">
            {rows.length} session{rows.length === 1 ? '' : 's'} in {dateGroups.length} date
            {dateGroups.length === 1 ? '' : 's'} · expand a date to view rows
          </p>
        </div>
        <button
          type="button"
          onClick={exportToCSV}
          disabled={visibleColumnsArray.length === 0}
          className="flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50"
        >
          <Download className="h-4 w-4" />
          Export
        </button>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setVisibleColumns(new Set(SESSION_COLUMNS.map((c) => c.key)))}
            className="rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
          >
            Show all
          </button>
          <button
            type="button"
            onClick={() => setVisibleColumns(new Set())}
            className="rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
          >
            Hide all
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {SESSION_COLUMNS.map((col) => (
            <button
              key={col.key}
              type="button"
              onClick={() => toggleColumn(col.key)}
              className={`flex items-center gap-1 rounded px-2 py-1 text-xs ${
                visibleColumns.has(col.key)
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-gray-100 text-gray-600 opacity-50'
              }`}
            >
              {visibleColumns.has(col.key) ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
              {col.label}
            </button>
          ))}
        </div>
      </div>

      <div className="table-responsive overflow-x-auto rounded-lg border border-gray-200 bg-white">
        {visibleColumnsArray.length === 0 ? (
          <p className="p-8 text-center text-sm text-gray-500">Turn on at least one column to see the table.</p>
        ) : (
          <table className="w-full min-w-[960px]">
            <thead className="border-b border-gray-200 bg-gray-50">
              <tr>
                {visibleColumnsArray.map((col) => (
                  <th key={col.key} className="whitespace-nowrap px-4 py-3 text-left text-sm font-semibold text-gray-700">
                    {col.label}
                  </th>
                ))}
                {(onRestoreSession || onDeleteSession) ? (
                  <th className="whitespace-nowrap px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumnsArray.length} className="px-4 py-8 text-center text-gray-500">
                    No sessions match your filters
                  </td>
                </tr>
              ) : (
                dateGroups.map((group) => {
                  const expanded = expandedDateGroups.has(group.iso);
                  return (
                    <React.Fragment key={group.iso || '__no_date__'}>
                      <tr
                        className="cursor-pointer border-b border-gray-200 bg-gray-100 hover:bg-gray-200"
                        onClick={() => toggleDateGroup(group.iso)}
                      >
                        <td
                          colSpan={visibleColumnsArray.length}
                          className="px-4 py-3 text-sm font-semibold text-gray-800"
                        >
                          <span className="inline-flex items-center gap-2">
                            <ChevronRight
                              className={`h-4 w-4 shrink-0 transition-transform ${expanded ? 'rotate-90' : ''}`}
                            />
                            {group.label}
                            <span className="font-normal text-gray-500">
                              ({group.rows.length} session{group.rows.length === 1 ? '' : 's'})
                            </span>
                          </span>
                        </td>
                      </tr>
                      {expanded &&
                        group.rows.map((row) => {
                          const selected = row.sessionId === selectedSessionId;
                          return (
                            <tr
                              key={row.sessionId}
                              onClick={() => onSelectSession(row.sessionId)}
                              className={`cursor-pointer border-b border-gray-200 hover:bg-gray-50 ${
                                selected ? 'bg-emerald-50' : ''
                              }`}
                            >
                              {visibleColumnsArray.map((col) => (
                                <td
                                  key={col.key}
                                  className="max-w-[240px] truncate px-4 py-3 text-sm text-gray-700"
                                  title={formatSessionCell(col.key, row)}
                                >
                                  {formatSessionCell(col.key, row)}
                                </td>
                              ))}
                              {(onRestoreSession || onDeleteSession) ? (
                                <td className="whitespace-nowrap px-4 py-3 text-sm">
                                  <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                                    {row.status === 'cancelled' && onRestoreSession ? (
                                      <button
                                        type="button"
                                        className="rounded border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800 hover:bg-emerald-100"
                                        onClick={() => onRestoreSession(row.sessionId)}
                                      >
                                        Restore
                                      </button>
                                    ) : null}
                                    {onDeleteSession ? (
                                      <button
                                        type="button"
                                        className="rounded border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 hover:bg-red-100"
                                        onClick={() => onDeleteSession(row.sessionId)}
                                      >
                                        Delete
                                      </button>
                                    ) : null}
                                  </div>
                                </td>
                              ) : null}
                            </tr>
                          );
                        })}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

interface TableViewProps {
  bookings: BookingWithCustomer[];
  onBookingClick: (booking: BookingWithCustomer) => void;
  onEditBooking?: (booking: BookingWithCustomer) => void;
  sortBy: string;
  onSortChange: (sortBy: string) => void;
  onStatusChange?: (bookingId: string, status: string) => void;
}

type ColumnKey = 'date' | 'duration' | 'company' | 'course' | 'instructor' | 'attendees' | 'status' | 'price';

export const TableView: React.FC<TableViewProps> = ({
  bookings,
  onBookingClick,
  onEditBooking,
  sortBy,
  onSortChange,
  onStatusChange
}) => {
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [visibleColumns, setVisibleColumns] = useState<Set<ColumnKey>>(
    new Set(['date', 'company', 'course', 'instructor', 'status'])
  );
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const columns: { key: ColumnKey; label: string; sortable: boolean }[] = [
    { key: 'date', label: 'Date', sortable: true },
    { key: 'duration', label: 'Duration', sortable: false },
    { key: 'company', label: 'Company', sortable: true },
    { key: 'course', label: 'Course', sortable: true },
    { key: 'instructor', label: 'Instructor', sortable: false },
    { key: 'attendees', label: 'Attendees', sortable: false },
    { key: 'status', label: 'Status', sortable: false },
    { key: 'price', label: 'Price', sortable: false }
  ];

  const visibleColumnsArray = columns.filter(c => visibleColumns.has(c.key));

  const toggleColumn = (key: ColumnKey) => {
    const newColumns = new Set(visibleColumns);
    if (newColumns.has(key)) {
      newColumns.delete(key);
    } else {
      newColumns.add(key);
    }
    setVisibleColumns(newColumns);
  };

  const toggleRowSelection = (bookingId: string) => {
    const newSelection = new Set(selectedRows);
    if (newSelection.has(bookingId)) {
      newSelection.delete(bookingId);
    } else {
      newSelection.add(bookingId);
    }
    setSelectedRows(newSelection);
  };

  const toggleAllRows = () => {
    if (selectedRows.size === bookings.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(bookings.map(b => b.id)));
    }
  };

  const exportToCSV = () => {
    const data = bookings
      .filter(b => selectedRows.size === 0 || selectedRows.has(b.id))
      .map(b => ({
        Date: formatDate(b.courseDate),
        Duration: `${getDaysDuration(b.courseDate, b.courseEndDate || b.courseDate)} days`,
        Company: b.customer?.companyName || 'Public',
        Course: b.courseName,
        Instructor: b.instructorName || '-',
        Attendees: b.attendeeCount || 0,
        Status: getBookingStatus(b),
        Price: b.totalPrice ? `€${Number(b.totalPrice).toFixed(2)}` : '-'
      }));

    const csv = Papa.unparse(data);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `bookings-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const sortedBookings = useMemo(() => {
    const sorted = [...bookings];
    sorted.sort((a, b) => {
      switch (sortBy) {
        case 'date-asc':
          return (a.courseDate || '').localeCompare(b.courseDate || '');
        case 'date-desc':
          return (b.courseDate || '').localeCompare(a.courseDate || '');
        case 'company':
          const compA = a.customer?.companyName || '';
          const compB = b.customer?.companyName || '';
          return compA.localeCompare(compB);
        case 'course':
          return (a.courseName || '').localeCompare(b.courseName || '');
        default:
          return 0;
      }
    });
    return sorted;
  }, [bookings, sortBy]);

  const handleSort = (key: ColumnKey) => {
    if (key === 'date') {
      onSortChange(sortBy === 'date-asc' ? 'date-desc' : 'date-asc');
    } else if (key === 'company') {
      onSortChange('company');
    } else if (key === 'course') {
      onSortChange('course');
    }
  };

  const getSortIcon = (key: ColumnKey) => {
    if (key === 'date' && sortBy === 'date-asc') return <ChevronUp className="w-4 h-4" />;
    if (key === 'date' && sortBy === 'date-desc') return <ChevronDown className="w-4 h-4" />;
    if (key === 'company' && sortBy === 'company') return <ChevronUp className="w-4 h-4" />;
    if (key === 'course' && sortBy === 'course') return <ChevronUp className="w-4 h-4" />;
    return null;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between bg-white p-4 rounded-lg border border-gray-200">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setVisibleColumns(new Set(columns.map(c => c.key)))}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Show All
          </button>
          <div className="flex gap-2 ml-4 flex-wrap">
            {columns.map(col => (
              <button
                key={col.key}
                onClick={() => toggleColumn(col.key)}
                className={`px-2 py-1 text-xs rounded flex items-center gap-1 ${
                  visibleColumns.has(col.key)
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-gray-100 text-gray-600 opacity-50'
                }`}
              >
                {visibleColumns.has(col.key) ? (
                  <Eye className="w-3 h-3" />
                ) : (
                  <EyeOff className="w-3 h-3" />
                )}
                {col.label}
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={exportToCSV}
          className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 flex items-center gap-2"
        >
          <Download className="w-4 h-4" />
          Export
        </button>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto table-responsive">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="w-12 px-4 py-3">
                <input
                  type="checkbox"
                  checked={selectedRows.size === bookings.length && bookings.length > 0}
                  onChange={toggleAllRows}
                  className="w-4 h-4 cursor-pointer"
                />
              </th>
              {visibleColumnsArray.map(col => (
                <th
                  key={col.key}
                  onClick={() => col.sortable && handleSort(col.key)}
                  className={`px-4 py-3 text-left text-sm font-semibold text-gray-700 ${
                    col.sortable ? 'cursor-pointer hover:bg-gray-100' : ''
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {col.label}
                    {col.sortable && getSortIcon(col.key as ColumnKey)}
                  </div>
                </th>
              ))}
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 w-12">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedBookings.length === 0 ? (
              <tr>
                <td colSpan={visibleColumnsArray.length + 2} className="px-4 py-8 text-center text-gray-500">
                  No bookings found
                </td>
              </tr>
            ) : (
              sortedBookings.map((booking) => {
                const status = getBookingStatus(booking);
                const colors = statusColors[status];
                const isExpanded = expandedRow === booking.id;

                return (
                  <React.Fragment key={booking.id}>
                    <tr className={`border-b border-gray-200 hover:bg-gray-50 ${selectedRows.has(booking.id) ? 'bg-emerald-50' : ''}`}>
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedRows.has(booking.id)}
                          onChange={() => toggleRowSelection(booking.id)}
                          className="w-4 h-4 cursor-pointer"
                        />
                      </td>
                      {visibleColumnsArray.map(col => (
                        <td key={col.key} className="px-4 py-3 text-sm text-gray-700">
                          {col.key === 'date' && formatDate(booking.courseDate)}
                          {col.key === 'duration' && `${getDaysDuration(booking.courseDate, booking.courseEndDate || booking.courseDate)} days`}
                          {col.key === 'company' && (booking.customer?.companyName || 'Public')}
                          {col.key === 'course' && booking.courseName}
                          {col.key === 'instructor' && (booking.instructorName || '-')}
                          {col.key === 'attendees' && (booking.attendeeCount || 0)}
                          {col.key === 'status' && (
                            <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${colors.light} ${colors.text}`}>
                              {status}
                            </span>
                          )}
                          {col.key === 'price' && (booking.totalPrice ? `€${Number(booking.totalPrice).toFixed(2)}` : '-')}
                        </td>
                      ))}
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setExpandedRow(isExpanded ? null : booking.id)}
                          className="p-2 hover:bg-gray-200 rounded"
                        >
                          <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <td colSpan={visibleColumnsArray.length + 2} className="px-4 py-4">
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <label className="font-semibold text-gray-700">Location</label>
                              <p className="text-gray-600">{booking.locationName || '-'}</p>
                            </div>
                            <div>
                              <label className="font-semibold text-gray-700">Contact</label>
                              <p className="text-gray-600">{booking.customer?.contact || '-'}</p>
                            </div>
                            <div>
                              <label className="font-semibold text-gray-700">Course End</label>
                              <p className="text-gray-600">{formatDate(booking.courseEndDate)}</p>
                            </div>
                            <div>
                              <label className="font-semibold text-gray-700">Notes</label>
                              <p className="text-gray-600">{booking.notes || '-'}</p>
                            </div>
                            <div className="col-span-2 flex gap-2">
                              {onEditBooking && (
                                <button
                                  onClick={() => onEditBooking(booking)}
                                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium flex items-center gap-2"
                                >
                                  <Pencil className="w-4 h-4" />
                                  Edit Booking
                                </button>
                              )}
                              <button
                                onClick={() => onBookingClick(booking)}
                                className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 text-sm font-medium"
                              >
                                View Full Details
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
